import { WORKFLOW_VARIABLE_LABELS } from "../../WorkflowVariables/workflowVariables.constants";
import { EXPRESSION_OPERATORS, WF_EXPRESSION_VARIABLE_GROUP_NAMES } from "../constants/expressionBuilder.constants";
import { evaluateRule } from "./expressionRuleEvaluation.utils";

export { evaluateRule };

/**
 * Generates a unique ID for expression nodes
 * @returns {string} Unique identifier
 * @author Abhishek Dhillon
 */
export const generateId = () => {
  return "expr_" + Math.random().toString(36).substr(2, 9);
};

/**
 * Gets operators based on field type, including standard functions
 * @param {string} fieldType - Type of the field (text, number, date, dropdown)
 * @returns {Array} Array of operators and standard functions
 * @author Abhishek Dhillon
 */
export const getOperatorsByType = (fieldType) => {
  let operators = [];

  // Get field-specific operators
  switch (fieldType) {
    case "text":
      operators = EXPRESSION_OPERATORS.STRING;
      break;
    case "number":
      operators = EXPRESSION_OPERATORS.NUMBER;
      break;
    case "date":
      operators = EXPRESSION_OPERATORS.DATE;
      break;
    case "dropdown":
      operators = EXPRESSION_OPERATORS.DROPDOWN;
      break;
    default:
      operators = EXPRESSION_OPERATORS.STRING;
  }

  // Combine standard functions with field-specific operators
  return operators;
};

/**
 * Recursively evaluates expression tree
 * @param {Object} dataObject - Data to evaluate
 * @param {Object} expressionNode - Expression node
 * @returns {boolean} Evaluation result
 * @author Abhishek Dhillon
 */
export const evaluateExpression = (dataObject, expressionNode) => {
  if (expressionNode?.type === "RULE") {
    return evaluateRule(dataObject, expressionNode);
  }

  if (expressionNode?.type === "GROUP" && expressionNode?.children?.length > 0) {
    const results = expressionNode.children.map((child) => evaluateExpression(dataObject, child));

    if (expressionNode.operation === "AND") {
      return results.every((result) => result === true);
    } else if (expressionNode.operation === "OR") {
      return results.some((result) => result === true);
    }
  }

  return true;
};

/**
 * Updates expression tree immutably
 * @param {Object} tree - Expression tree
 * @param {string} targetId - Target node ID
 * @param {Function} updateFn - Update function
 * @returns {Object} Updated tree
 * @author Abhishek Dhillon
 */
export const updateExpressionTree = (tree, targetId, updateFn) => {
  if (tree?.id === targetId) {
    return updateFn(tree);
  }

  if (tree?.type === "GROUP" && tree?.children) {
    const newChildren = tree.children.map((child) => updateExpressionTree(child, targetId, updateFn)).filter((child) => child !== null);

    return { ...tree, children: newChildren };
  }

  return tree;
};

/**
 * Transforms workflow variables to expression builder format
 * @param {Array} customWorkflowVariables - Workflow variables from API
 * @param {Array} userChips - User chips from API
 * @returns {Array} Transformed fields for expression builder
 * @author Abhishek Dhillon
 */
export const transformWorkflowVariables = (customWorkflowVariables, userChips, adminCustomVariables = [], priorityList = []) => {
  const taskChipsWithPriority = [
    ...taskChips,
    {
      id: "taskvariables_Priority_no-id",
      name: "Priority",
      type: "text",
      group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.TASK_CHIPS,
      options: priorityList,
    },
  ]
  return [
    ...(customWorkflowVariables
      ?.sort((a, b) => a?.name?.localeCompare(b?.name))
     
      ?.map((variable) => ({
        ...variable,
        options: variable?.attributes?.options || variable?.options || [],
        group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.FORM_SMARTCHIPS,
        linkedVarName:
          variable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD
            ? variable?.adminCustomVariables
              ? adminCustomVariables?.find(
                  (adminVariable) => adminVariable?.adminCustomVariables === variable?.adminCustomVariables && variable?.adminCustomVariables !== null
                )?.variableName
              : "Active Members"
            : "",
      })) || []),
    ...(userChips
      ?.sort((a, b) => a?.name?.localeCompare(b?.name))
      ?.map((chip) => ({
        id: chip?._id,
        name: chip?.name,
        type: chip?.type || "text",
        attributes: chip?.attributes,
        options: chip?.options || [],
        chipType: chip?.chipType,
        fixedVariableId: chip?.fixedVariableId,
        variableId: chip?.variableId,
        group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.CREATOR_CHIPS,
      })) || []),
    ...(userChips
      ?.sort((a, b) => a?.name?.localeCompare(b?.name))
      ?.map((chip) => ({
        id: chip?._id,
        name: chip?.name,
        type: chip?.type || "text",
        attributes: chip?.attributes,
        options: chip?.options || [],
        chipType: chip?.chipType,
        fixedVariableId: chip?.fixedVariableId,
        variableId: chip?.variableId,
        group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.ASSIGNEE_CHIPS,
      })) || []),
    ...taskChipsWithPriority,
    ...emailChips,
  ];
};

const emailChips = [
  {
    id: "emailchips_SenderEmail_no-id",
    name: "Sender Email",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.EMAIL_CHIPS,
  },
  {
    id: "emailchips_SenderName_no-id",
    name: "Sender Name",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.EMAIL_CHIPS,
  },
  {
    id: "emailchips_ReferenceId_no-id",
    name: "Reference Id",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.EMAIL_CHIPS,
  },
  {
    id: "emailchips_UniqueId_no-id",
    name: "Unique Id",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.EMAIL_CHIPS,
  },
]

const taskChips = [
  {
    id: "taskvariables_TaskId_no-id",
    name: "Task Id",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.TASK_CHIPS,
  },
  {
    id: "taskvariables_TaskName_no-id",
    name: "Task Name",
    type: "text",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.TASK_CHIPS,
  },
  {
    id: "taskvariables_TaskDueDate_no-id",
    name: "Task Due Date",
    type: "date",
    group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.TASK_CHIPS,
  },
];

/**
 * Creates a new rule with default values
 * @param {Array} availableFields - Available fields
 * @returns {Object} New rule object
 * @author Abhishek Dhillon
 */
export const createNewRule = (availableFields) => {
  return {
    id: generateId(),
    type: "RULE",
    field: null,
    operator: null,
    value: "",
  };
};

/**
 * Creates a new group with default rule
 * @param {Array} availableFields - Available fields
 * @returns {Object} New group object
 * @author Abhishek Dhillon
 */
export const createNewGroup = (availableFields) => {
  return {
    id: generateId(),
    type: "GROUP",
    operation: "AND",
    children: [createNewRule(availableFields)],
  };
};

export const transformVariablesToFields = (customWorkflowVariables, userChips, adminCustomVariables) => {
  return [
    ...(customWorkflowVariables
      ?.sort((a, b) => a?.name?.localeCompare(b?.name))
      ?.map((variable) => ({
        ...variable,
        options: variable?.attributes?.options || variable?.options || [],
        group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.FORM_SMARTCHIPS,
        linkedVarName:
          variable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD
            ? variable?.adminCustomVariables
              ? adminCustomVariables?.find(
                  (adminVariable) => adminVariable?.adminCustomVariables === variable?.adminCustomVariables && variable?.adminCustomVariables !== null
                )?.variableName
              : "Active Members"
            : "",
      })) || []),    
      ...(userChips
        ?.sort((a, b) => a?.name?.localeCompare(b?.name))
        ?.map((chip) => ({
          id: chip?._id,
          name: chip?.name,
          type: chip?.type || "text",
          attributes: chip?.attributes,
          options: chip?.options || [],
          chipType: chip?.chipType,
          fixedVariableId: chip?.fixedVariableId,
          variableId: chip?.variableId,
          group: WF_EXPRESSION_VARIABLE_GROUP_NAMES.USER_CHIPS,
        })) || [])];
};